import React from 'react';

import { ContactsOutlined, SellOutlined } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { Box } from '@mui/material';

import Accordion from '~/components/v2/common/Accordion/Accordion';
import ReadOnlyField from '~/components/v2/common/ReadOnlyField/ReadOnlyField';

interface Props {
  opportunityInfo: App.OpportunityInfo;
  keyContacts: App.KeyContacts;
  keyCommercial: App.KeyCommercial;
  isAdmin: boolean;
  accountName: string;
}

export default function OpportunityInformation({
  opportunityInfo,
  keyContacts,
  keyCommercial,
  isAdmin,
  accountName,
}: Props) {
  const {
    merchant_account_manager_first_name__c,
    merchant_account_manager_last_name__c,
    opp_owner_lookup_name__c,
    name,
    campaign_type__c,
    le_offer_id__c,
  } = opportunityInfo || {};
  const {
    escalation_nameescape__c,
    primary_contact_first_name__c,
    primary_contact_last_name__c,
    escalation_email__c,
    primary_contact_email__c,
    escalation_phone__c,
    booking_email__c,
    reservations_phone__c,
    system_manager_name__c,
    system_manager_email__c,
    system_manager_phone__c,
  } = keyContacts || {};

  const {
    tripadvisor_link__c,
    accelerated_margin__c,
    refunds_not_permitted__c,
    reason_4_brand_exclusion__c,
    countries_not_to_run__c,
    reason_4_country_exclusion__c,
    brand_not_running__c,
    deal_tier__c,
    redemption_options__c,
  } = keyCommercial || {};

  return (
    <>
      <Box mt={2}>
        <Accordion title="Opportunity Information" icon={<StarOutlineIcon />}>
          <ReadOnlyField
            title="Campaign/Market Manager"
            value={
              !merchant_account_manager_first_name__c && !merchant_account_manager_last_name__c
                ? '-'
                : `${merchant_account_manager_first_name__c || ''} ${
                    merchant_account_manager_last_name__c || ''
                  }`.trim()
            }
            type="text"
            copyable
            icon={<PersonIcon />}
          />
          <ReadOnlyField
            title="Opportunity Owner"
            value={opp_owner_lookup_name__c || '-'}
            type="text"
            copyable
            icon={<PersonIcon />}
          />
          <ReadOnlyField title="Account Name" value={accountName || '-'} type="text" />
          <ReadOnlyField title="Opportunity Name" value={name || '-'} type="text" />
          <ReadOnlyField title="Campaign Type" value={campaign_type__c || '-'} type="text" />
          <ReadOnlyField title="LE Offer ID" value={le_offer_id__c || '-'} type="text" />
        </Accordion>
      </Box>

      <Box id="key-contacts" mt={2}>
        <Accordion title="Key contacts" icon={<ContactsOutlined />}>
          <ReadOnlyField
            title="Escalation Name"
            value={!escalation_nameescape__c ? '-' : escalation_nameescape__c}
            type="text"
          />
          <ReadOnlyField
            title="Primary Contact Name"
            value={
              !primary_contact_first_name__c && !primary_contact_last_name__c
                ? '-'
                : `${primary_contact_first_name__c || ''} ${primary_contact_last_name__c || ''}`.trim()
            }
            type="text"
          />
          <ReadOnlyField title="Escalation Email" value={escalation_email__c || '-'} copyable type="text" />
          <ReadOnlyField title="Primary Contact Email" value={primary_contact_email__c || '-'} copyable type="text" />
          <ReadOnlyField
            title="Escalation Phone"
            value={escalation_phone__c || '-'}
            copyable
            copyableType="number"
            type="text"
          />
          {isAdmin && (
            <ReadOnlyField title="System Manager Name" value={system_manager_name__c || '-'} copyable type="text" />
          )}
          <ReadOnlyField title="Reservation Email" value={booking_email__c || '-'} copyable type="text" />
          {isAdmin && (
            <ReadOnlyField title="System Manager Email" value={system_manager_email__c || '-'} copyable type="text" />
          )}
          <ReadOnlyField
            title="Reservation Phone"
            value={reservations_phone__c || '-'}
            copyable
            copyableType="number"
            type="text"
          />
          {isAdmin && (
            <ReadOnlyField
              title="System Manager Phone"
              value={system_manager_phone__c || '-'}
              copyable
              copyableType="number"
              type="text"
            />
          )}
        </Accordion>
      </Box>

      <Box id="key-commercial" mt={2}>
        <Accordion title="Key commercial" icon={<SellOutlined />}>
          <ReadOnlyField title="Offer tier" value={deal_tier__c || '-'} type="text" />
          <ReadOnlyField title="Connection/Redemption Type" value={redemption_options__c || '-'} type="text" />
          {tripadvisor_link__c ? (
            <ReadOnlyField title="TripAdvisor Link" value="View link" type="link" href={tripadvisor_link__c} />
          ) : (
            <ReadOnlyField title="TripAdvisor Link" value="-" type="text" />
          )}
          {isAdmin && (
            <ReadOnlyField title="Performance Override" value={accelerated_margin__c?.toString() || '-'} type="text" />
          )}
          <ReadOnlyField title="Refunds Permitted" value={refunds_not_permitted__c?.toString() || '-'} type="text" />
          <ReadOnlyField
            title="Excluded Brands"
            value={brand_not_running__c.length > 0 ? brand_not_running__c : '-'}
            type="tags"
          />
          <ReadOnlyField
            title="Excluded Countries"
            value={countries_not_to_run__c.length > 0 ? countries_not_to_run__c : '-'}
            type="tags"
          />
          <ReadOnlyField title="Reason for Excluded Brands" value={reason_4_brand_exclusion__c || '-'} type="text" />
          <ReadOnlyField
            title="Reason for Excluded Countries"
            value={reason_4_country_exclusion__c || '-'}
            type="text"
          />
        </Accordion>
      </Box>
    </>
  );
}
